/**
 * AccountSummary Component
 * Displays total balance summary card with breakdown by type
 */
import { cn } from '@/utils/cn';
import { formatCurrency } from '@/utils/format';
import Card from '@/components/ui/Card';
import AccountTypeIcon, { getAccountTypeName } from './AccountTypeIcon';
import type { BalanceSummary } from '@/services/accounts';
import type { AccountType } from '@/types';

export interface AccountSummaryProps {
  summary: BalanceSummary;
  className?: string;
}

export default function AccountSummary({ summary, className }: AccountSummaryProps) {
  const hasLiabilities = summary.totalLiabilities > 0;

  return (
    <Card className={cn('', className)}>
      {/* Net worth header */}
      <div className="text-center mb-6">
        <p className="text-sm font-medium text-gray-500">Net Worth</p>
        <p
          className={cn(
            'text-3xl font-bold mt-1 tabular-nums',
            summary.netWorth < 0 ? 'text-red-600' : 'text-gray-900'
          )}
        >
          {formatCurrency(summary.netWorth)}
        </p>
      </div>

      {/* Assets vs Liabilities - stacked vertically for readability */}
      <div className="space-y-3 mb-6 pb-6 border-b border-gray-200">
        <div className="flex items-center justify-between py-2 px-3 rounded-lg bg-green-50">
          <p className="text-sm font-medium text-gray-700">Assets</p>
          <p className="text-lg font-semibold text-green-600 tabular-nums">
            {formatCurrency(summary.totalAssets)}
          </p>
        </div>
        <div className="flex items-center justify-between py-2 px-3 rounded-lg bg-red-50">
          <p className="text-sm font-medium text-gray-700">Liabilities</p>
          <p
            className={cn(
              'text-lg font-semibold tabular-nums',
              hasLiabilities ? 'text-red-600' : 'text-gray-500'
            )}
          >
            {hasLiabilities
              ? `-${formatCurrency(summary.totalLiabilities)}`
              : formatCurrency(0)}
          </p>
        </div>
      </div>

      {/* Breakdown by type */}
      <div className="space-y-3">
        <p className="text-sm font-medium text-gray-700">Breakdown by Type</p>
        {summary.byType.length === 0 ? (
          <p className="text-sm text-gray-500">No accounts connected</p>
        ) : (
          <div className="space-y-2">
            {summary.byType.map(({ type, total, count }) => {
              // For liability accounts (credit, loan), display as negative
              const isLiability = type === 'credit' || type === 'loan';
              const displayTotal = isLiability ? -Math.abs(total) : total;

              return (
                <div
                  key={type}
                  className="flex items-center justify-between py-2 px-3 rounded-lg bg-gray-50"
                >
                  <div className="flex items-center gap-3">
                    <AccountTypeIcon type={type as AccountType} size="sm" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {getAccountTypeName(type as AccountType)}
                      </p>
                      <p className="text-xs text-gray-500">
                        {count} account{count !== 1 ? 's' : ''}
                      </p>
                    </div>
                  </div>
                  <p
                    className={cn(
                      'font-medium tabular-nums',
                      displayTotal < 0 ? 'text-red-600' : 'text-gray-900'
                    )}
                  >
                    {formatCurrency(displayTotal)}
                  </p>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Card>
  );
}

/**
 * Loading skeleton for AccountSummary
 */
export function AccountSummarySkeleton() {
  return (
    <Card>
      {/* Net worth skeleton */}
      <div className="text-center mb-6 animate-pulse">
        <div className="h-4 w-20 bg-gray-200 rounded mx-auto" />
        <div className="h-9 w-40 bg-gray-200 rounded mx-auto mt-2" />
      </div>

      {/* Assets vs Liabilities skeleton */}
      <div className="space-y-3 mb-6 pb-6 border-b border-gray-200 animate-pulse">
        <div className="flex items-center justify-between py-2 px-3 rounded-lg bg-gray-50">
          <div className="h-4 w-16 bg-gray-200 rounded" />
          <div className="h-6 w-24 bg-gray-200 rounded" />
        </div>
        <div className="flex items-center justify-between py-2 px-3 rounded-lg bg-gray-50">
          <div className="h-4 w-16 bg-gray-200 rounded" />
          <div className="h-6 w-24 bg-gray-200 rounded" />
        </div>
      </div>

      {/* Breakdown skeleton */}
      <div className="space-y-3 animate-pulse">
        <div className="h-4 w-32 bg-gray-200 rounded" />
        <div className="space-y-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="flex items-center justify-between py-2 px-3 rounded-lg bg-gray-50">
              <div className="flex items-center gap-3">
                <div className="h-8 w-8 bg-gray-200 rounded-lg" />
                <div>
                  <div className="h-4 w-20 bg-gray-200 rounded" />
                  <div className="h-3 w-16 bg-gray-200 rounded mt-1" />
                </div>
              </div>
              <div className="h-5 w-24 bg-gray-200 rounded" />
            </div>
          ))}
        </div>
      </div>
    </Card>
  );
}
